/**
 * How each thing a board records is carried to the platform.
 *
 * The write twin of `adapt.ts`. A board calls `recorded(kind, rec, extra)`
 * after its own local push; `LegacyWrites` looks the kind up here and sends it.
 * Every engine-record → API-body mapping lives in one place per area
 * (`writes/<area>.ts`), registered at import, so each is unit-tested on its
 * own and no board has to know the platform exists.
 *
 * Nothing here imports the demo domain: this sits in the entry chunk.
 */
import type { QueryKey } from "@tanstack/react-query";
import type { DataSource, Principal } from "../../api/dataSource";

/** How the platform took a record, stamped onto it as `rec.platform`. */
export interface RecordStamp {
  state: "saving" | "saved" | "refused";
  /** minted once per record and reused on retry, so an idempotent endpoint files it once */
  key: string;
  /** the platform's id (uuid) and its human reference, once saved */
  id?: string;
  reference?: string;
  at?: string;
  /** which register took it, where an area writes to more than one */
  kind?: string;
  why?: string;
}

/** What a write returns once the platform has the record. */
export interface WriteAck {
  id: string;
  reference: string;
  at?: string;
  kind?: string;
  /** what to say; "{reference} saved on the platform." when left out */
  text?: string;
  /** a saved record that still needs a person's eye (a notification owed, a half-written record) */
  tone?: "ok" | "warn";
}

export interface WriteContext {
  source: DataSource;
  siteId: string;
  principal: Principal;
  /** the record's idempotency key */
  key: string;
}

export interface WriteSpec {
  /**
   * The permission the platform will ask for, so a refusal can be said before
   * the request rather than after it. Advisory only: the platform enforces.
   */
  permission?: (rec: any, extra: any) => string | undefined;
  /** What to refresh once it is saved. */
  invalidate: (siteId: string) => QueryKey[];
  send(ctx: WriteContext, rec: any, extra: any): Promise<WriteAck>;
  /** True when the endpoint honours Idempotency-Key, so TRY AGAIN cannot file it twice. */
  retryable?: boolean;
}

const WRITES = new Map<string, WriteSpec>();

/** Called once per kind, at import, by the area's own file. */
export function registerWrite(kind: string, spec: WriteSpec): void {
  WRITES.set(kind, spec);
}

export const writeFor = (kind: string): WriteSpec | undefined => WRITES.get(kind);

/** The kinds registered, for tests. */
export const writeKinds = (): string[] => [...WRITES.keys()];
